import * as React from 'react';
import {Query} from 'react-apollo';
import {Helmet} from 'react-helmet';

// @ts-expect-error
import memoize from 'memoize-one';

import {translateConnectionObjectToArray} from 'nutshell-core';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {headTitle} from 'shells/document/head-title';
import type {DateHeader} from 'shells/email-row-item/types';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';

import type {
    GetSentEmails as GetSentEmailsQuery,
    GetSentEmailsVariables as GetSentEmailsQueryVariables,
    GetSendingEmails as GetSendingEmailsQuery,
    GetSendingEmailsVariables as GetSendingEmailsQueryVariables,
    QueuedEmail as QueuedEmailFragment,
    Email as EmailFragment,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../apollo-client';

import {EmailSetupCta} from '../../email-setup-cta';

import {EmailList} from '../email-list';
import GET_SENDING_EMAILS from '../graphql/queries/get-sending-emails.graphql';
import GET_SENT_EMAILS from '../graphql/queries/get-sent-emails.graphql';
import {QueuedEmailRowItem} from '../scheduled-mail/queued-email-row-item';
import {addHeaders, ERROR_STATE} from '../utils';

import {SentEmailRowItem} from './sent-email-row-item';

import styles from '../email-list.module.css';

const LIMIT = 50;

type WithSentMail = {
    isLoading: boolean;
    emails: EmailFragment[];
    hasNextPage: boolean;
    loadNextPage: () => Promise<any>;
    errorMessage: string | null | undefined;
};

type WithSendingMail = {
    sendingEmails: QueuedEmailFragment[];
};

type Props = WithSentMail & WithSendingMail;

export class SentMailComponent extends React.PureComponent<Props> {
    addHeadersToEmails: (arg1: EmailFragment[]) => EmailFragment | DateHeader[];

    constructor() {
        // @ts-expect-error
        super();
        // @ts-expect-error
        this.addHeadersToEmails = memoize((emails) => addHeaders(emails));
    }

    render() {
        const emails = this.addHeadersToEmails(this.props.emails);
        const isEmailConfigured = NutClientConfig.hasConfiguredEmail;

        const sentEmptyStateConfigured = (
            <EmptyStateSmall
                title='You haven’t sent any email from Nutshell'
                subtitle='Once you start sending email from Nutshell, they’ll show up here'
                icon={<img alt='' src='/include/images/email-drip/email-sent_2x.png' />}
            />
        );
        const sentEmptyStateUnconfigured = (
            <EmptyStateLarge
                title='Send emails from Nutshell'
                subtitle='Powerful email capabilities, all in one place.'
                detailTextBullets={[
                    'Create email templates that include automatic, timed follow-up messages.',
                    'Track open rates, create reusable email templates, and so much more, all natively built into Nutshell.',
                ]}
                cta={<EmailSetupCta />}
                icon={
                    <img
                        alt=''
                        src='/include/images/email-marketing/email_sent_empty_state.png'
                        className={styles['large-icon']}
                    />
                }
            />
        );

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle('Sent emails')}</title>
                </Helmet>
                <EmailList
                    isRequesting={this.props.isLoading}
                    onFetchMoreRows={this.props.loadNextPage}
                    hasNextPage={this.props.hasNextPage}
                    // @ts-expect-error
                    collection={this.props.sendingEmails.concat(emails)}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(this.props.errorMessage),
                            component: ERROR_STATE,
                        },
                        {
                            shouldRender: () => !isEmailConfigured,
                            component: sentEmptyStateUnconfigured,
                        },
                        {
                            // @ts-expect-error
                            shouldRender: () => emails.length === 0 && !this.props.isLoading,
                            component: sentEmptyStateConfigured,
                        },
                    ]}
                    renderRow={(emailItem: any, isFirstOfDate: boolean) => {
                        if (emailItem.__typename === 'EmailQueued') {
                            return (
                                <QueuedEmailRowItem
                                    key={emailItem.id}
                                    queuedEmail={emailItem}
                                    isFirstOfDate={isFirstOfDate}
                                    // @ts-expect-error
                                    onSequenceDeleted={updateCacheFromDeleteSendingEmail}
                                />
                            );
                        }

                        return (
                            // @ts-expect-error
                            <SentEmailRowItem
                                key={emailItem.id}
                                email={emailItem}
                                isFirstOfDate={isFirstOfDate}
                                onEmailSharedChange={updateCacheFromUpdate}
                                onEmailDeleted={updateCacheFromDeleteSentEmail}
                            />
                        );
                    }}
                />
            </React.Fragment>
        );
    }
}

// Called when a currently "sending" email is cancelled. This is rare, but needs
// to be accounted for.
// @ts-expect-error
function updateCacheFromDeleteSendingEmail(proxy, {data}) {
    const emailSequenceDelete = data && data.emailSequenceDelete;
    if (!emailSequenceDelete) return;

    const deletedSequenceId = emailSequenceDelete.emailSequence.id;

    // Read the data from our cache for this query.
    const cacheResult: GetSendingEmailsQuery | null | undefined = nutApolloClient.readQuery({
        query: GET_SENDING_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!cacheResult) return;

    cacheResult.sendingEmails.edges = cacheResult.sendingEmails.edges.filter(
        (edge) =>
            edge.node && edge.node.emailSequence && edge.node.emailSequence.id !== deletedSequenceId
    );

    // Write our data back to the cache.
    nutApolloClient.writeQuery({
        query: GET_SENDING_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data: cacheResult,
    });
}

// @ts-expect-error
function updateCacheFromDeleteSentEmail(proxy, {data}) {
    const emailDelete = data && data.emailDelete;
    if (!emailDelete) return;

    const deletedEmailId = emailDelete.email.id;

    // Read the data from our cache for this query.
    const cacheResult: GetSentEmailsQuery | null | undefined = nutApolloClient.readQuery({
        query: GET_SENT_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });
    if (!cacheResult) return;

    // Remove deleted items from cache
    cacheResult.sentEmails.edges = cacheResult.sentEmails.edges.filter(
        (edge) => edge.node && edge.node.id && edge.node.id !== deletedEmailId
    );

    // Write our data back to the cache.
    nutApolloClient.writeQuery({
        query: GET_SENT_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data: cacheResult,
    });
}

// @ts-expect-error
function updateCacheFromUpdate(proxy, {data}) {
    const emailShareAndCreateContacts = data && data.emailShareAndCreateContacts;
    if (!emailShareAndCreateContacts) return;
    const updatedEmailId = emailShareAndCreateContacts.email.id;

    // Read the data from our cache for this query.
    const cacheResult: GetSentEmailsQuery | null | undefined = nutApolloClient.readQuery({
        query: GET_SENT_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!cacheResult) return;

    // Update our new email item
    cacheResult.sentEmails.edges = cacheResult.sentEmails.edges.map((edge) => {
        if (edge.node && edge.node.id && edge.node.id === updatedEmailId) {
            return {
                __typename: 'EmailsEdge',
                node: {...edge.node, isShared: emailShareAndCreateContacts.email.isShared},
                cursor: edge.cursor,
            };
        }

        return edge;
    });

    // Write our data back to the cache.
    nutApolloClient.writeQuery({
        query: GET_SENT_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data: cacheResult,
    });
}

class SentQuery extends Query<GetSentEmailsQuery, GetSentEmailsQueryVariables> {}
class SendingQuery extends Query<GetSendingEmailsQuery, GetSendingEmailsQueryVariables> {}

export const SentMail = () => (
    <SentQuery
        query={GET_SENT_EMAILS}
        variables={{limit: LIMIT}}
        // isLoading only changes on `loadNextPage` if this option is true
        notifyOnNetworkStatusChange={true}
    >
        {({data: sentData, loading, networkStatus, error, refetch, fetchMore}) => {
            const sentEmails = sentData ? sentData.sentEmails : null;

            return (
                <SendingQuery
                    query={GET_SENDING_EMAILS}
                    variables={{limit: LIMIT}}
                    // isLoading only changes on `loadNextPage` if this option is true
                    notifyOnNetworkStatusChange={true}
                >
                    {({data: sendingData}) => {
                        const sendingEmails = sendingData ? sendingData.sendingEmails : null;

                        return (
                            <SentMailComponent
                                isLoading={loading}
                                // @ts-expect-error
                                onRefetch={refetch}
                                // @ts-expect-error
                                errorMessage={safelyGetGraphQLErrorMessage(error)}
                                networkStatus={networkStatus}
                                hasNextPage={sentEmails ? sentEmails.pageInfo.hasNextPage : true}
                                sendingEmails={
                                    sendingEmails
                                        ? translateConnectionObjectToArray(sendingEmails)
                                        : []
                                }
                                emails={
                                    sentEmails ? translateConnectionObjectToArray(sentEmails) : []
                                }
                                loadNextPage={() =>
                                    fetchMore({
                                        variables: {
                                            limit: LIMIT,
                                            cursor: sentEmails
                                                ? sentEmails.pageInfo.endCursor
                                                : null,
                                        },
                                        updateQuery: (
                                            previousResult: GetSentEmailsQuery,
                                            {fetchMoreResult}
                                        ) => {
                                            if (!fetchMoreResult) return previousResult;
                                            const previousEdges = previousResult.sentEmails.edges;
                                            const newEdges = fetchMoreResult.sentEmails.edges;
                                            const newTotalCount =
                                                fetchMoreResult.sentEmails.totalCount;
                                            const newPageInfo = fetchMoreResult.sentEmails.pageInfo;

                                            return {
                                                // Put the new emails at the end of the list and update `pageInfo`
                                                // so we have the new `endCursor` and `hasNextPage` values
                                                sentEmails: {
                                                    __typename: 'EmailsConnection',
                                                    totalCount: newTotalCount,
                                                    pageInfo: newPageInfo,
                                                    edges: [...previousEdges, ...newEdges],
                                                },
                                            };
                                        },
                                    })
                                }
                            />
                        );
                    }}
                </SendingQuery>
            );
        }}
    </SentQuery>
);
